import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import dayjs from 'dayjs'
import classNames from 'classnames'

import ChannelRow from './ChannelRow'
import TimeLine from './TimeLine'
import Schedule from './Schedule'
import Channels from './Channels'
import Schedules from './Schedules'
import useCurrentTime from '../../../../shared/hooks/useCurrentTime'
import {
  type FocusDetails,
  FocusContext,
  setFocus,
} from '@noriginmedia/norigin-spatial-navigation'
import { useHybridFocusable } from '../../hooks/useHybridFocusable'
import { getNextDaySchedulesByStation } from '../../actions/contentActions'
import tts from '../../lib/TTS'
import { useSidePanel } from '../SidePanel/SidePanelProvider'
import {
  getPlayingListing,
  getQueryParam,
  noOp,
  platformTransitionStyle,
  throttle,
} from '../../../../shared/helpers'
import {
  KeyTypeEnum,
  useKeyDown,
} from '../../../../shared/providers/KeyDownProvider'

import type { Channel, Listing } from '../../reducers/types/reducerTypes'
import { type EPGProps } from './types'
import type { Dayjs } from 'dayjs'
import { filterListings } from './util/filterListings'

const DEFAULT_SLOT_DURATION_MINUTES = 30
const DEFAULT_HOURS_TO_DISPLAY = 2
const CHANNEL_HEIGHT_PX = 200
const CHANNEL_ROW_MARGIN_BOTTOM_PX = 17
const COMPLETE_CHANNELS_VISIBLE = 2

const EPG = ({
  channels = [],
  onChannelEnterPress = noOp,
  onChannelFocused = noOp,
  onListingFocused = noOp,
  className = '',
  stealFocus = false,
}: EPGProps) => {
  const currentTime = useCurrentTime()
  const [scheduleRange, setScheduleRange] = useState<{
    start: Dayjs
    end: Dayjs
  }>()
  const [timeOffsetMinutes, setTimeOffsetMinutes] = useState(0)
  const [focusedChannel, setFocusedChannel] = useState<Channel | undefined>()
  const [focusedListing, setFocusedListing] = useState<Listing | undefined>()
  const [isEPGLoaded, setIsEPGLoaded] = useState<boolean>(false)
  const [firstVisibleChannelIndex, setFirstVisibleChannelIndex] = useState(0)
  const [translateY, setTranslateY] = useState(0)
  const focusedOnListingsGridRef = useRef(false)
  const nextDayChannelsRequested = useRef(false)
  const channelFeedCID =
    getQueryParam(location.search, 'channelFeedCID') || undefined
  const { ref, focusKey, hasFocusedChild } = useHybridFocusable({
    focusKey: 'EPG',
    trackChildren: true,
    preferredChildFocusKey: channelFeedCID,
  })

  const { isSidePanelFocused } = useSidePanel()

  useEffect(() => {
    if (
      isEPGLoaded &&
      (stealFocus || !isSidePanelFocused) &&
      !hasFocusedChild &&
      channels.length > 0
    ) {
      setFocus('EPG')
    }
  }, [isEPGLoaded])

  useEffect(() => {
    const eightPM = dayjs().hour(20)
    if (
      !nextDayChannelsRequested.current &&
      (scheduleRange?.start.isSame(eightPM) ||
        scheduleRange?.start.isAfter(eightPM))
    ) {
      getNextDaySchedulesByStation()
      nextDayChannelsRequested.current = true
    }
  }, [scheduleRange?.start])

  useEffect(() => {
    const eightPM = dayjs().hour(20)
    if (
      !nextDayChannelsRequested.current &&
      (scheduleRange?.start.isSame(eightPM) ||
        scheduleRange?.start.isAfter(eightPM))
    ) {
      getNextDaySchedulesByStation()
      nextDayChannelsRequested.current = true
    }
  }, [scheduleRange?.start])

  useEffect(() => {
    setTimeout(() => {
      if (!channelFeedCID) {
        setFirstVisibleChannelIndex(0)
        setTranslateY(0)
      }
    }, 100)
  }, [])

  useKeyDown(
    KeyTypeEnum.Back,
    () => {
      if (focusedOnListingsGridRef.current && focusedChannel) {
        setTimeOffsetMinutes(0)
        setFocus(focusedChannel.feed_cid)
      }
    },
    {
      stopPropagation: timeOffsetMinutes !== 0,
    }
  )
  const timeIndicatorPosition = useMemo(() => {
    let currentTimeMinutes = currentTime.minute()
    currentTimeMinutes =
      currentTimeMinutes > DEFAULT_SLOT_DURATION_MINUTES
        ? currentTimeMinutes - DEFAULT_SLOT_DURATION_MINUTES
        : currentTimeMinutes
    const minutes = currentTimeMinutes - timeOffsetMinutes
    const slotsQuantity =
      (DEFAULT_HOURS_TO_DISPLAY * 60) / DEFAULT_SLOT_DURATION_MINUTES
    return (minutes * (100 / slotsQuantity)) / DEFAULT_SLOT_DURATION_MINUTES
  }, [currentTime, timeOffsetMinutes])

  const baseTime = useMemo<Dayjs>(
    () => currentTime.add(timeOffsetMinutes, 'minute'),
    [currentTime, timeOffsetMinutes]
  )

  const handleChannelEnterPress = useCallback(
    (extraProps: { index: number; channel: Channel }) => {
      const { channel } = extraProps
      tts.stopRead()
      tts.pushTextToRead(`Selected channel ${channel.full_name}`)
      onChannelEnterPress(
        channel,
        getPlayingListing(channel, baseTime.toDate())
      )
    },
    [baseTime]
  )

  const handleChannelFocused = useCallback(
    (
      _,
      extraProps: {
        index: number
        channel: Channel
        focusedOnListingsGrid: boolean
      },
      details: FocusDetails
    ) => {
      const key = (details?.event as KeyboardEvent)?.key
      const { index, channel, focusedOnListingsGrid } = extraProps
      focusedOnListingsGridRef.current = focusedOnListingsGrid
      setFocusedChannel(channel)
      onChannelFocused(channel, focusedOnListingsGrid)
      if (!focusedOnListingsGrid) {
        onListingFocused(getPlayingListing(channel, baseTime.toDate()), false)
      }

      if (
        index > firstVisibleChannelIndex &&
        index >= COMPLETE_CHANNELS_VISIBLE
      ) {
        const offset =
          (index - COMPLETE_CHANNELS_VISIBLE + 1) *
          (CHANNEL_HEIGHT_PX + CHANNEL_ROW_MARGIN_BOTTOM_PX)
        setTranslateY(-offset)
        setFirstVisibleChannelIndex(index - 1)
      } else if (index < firstVisibleChannelIndex) {
        const offset =
          (index - COMPLETE_CHANNELS_VISIBLE + 2) *
          (CHANNEL_HEIGHT_PX + CHANNEL_ROW_MARGIN_BOTTOM_PX)
        setTranslateY(-offset)
        setFirstVisibleChannelIndex(index)
      }

      if (key === 'ArrowLeft' && timeOffsetMinutes > 0) {
        setTimeOffsetMinutes(0)
      }
    },
    [baseTime, timeOffsetMinutes, firstVisibleChannelIndex]
  )

  const handleListingFocused = useCallback(
    (_, extraProps: { channel: Channel; listing: Listing }, details) => {
      const { listing } = extraProps
      focusedOnListingsGridRef.current = true
      setFocusedChannel(extraProps.channel)
      setFocusedListing(listing)
      onListingFocused(listing, true)
      const startTime = dayjs(listing.start_time)
      const endTime = startTime.add(listing.duration / 60, 'minute')

      if (
        details.event?.code === 'ArrowRight' &&
        (endTime.isAfter(scheduleRange?.end.add(-1, 'minute')) ||
          startTime.isAfter(scheduleRange?.end.add(-30, 'minute'))) &&
        !(
          startTime.add(-1, 'minute').isBefore(scheduleRange?.start) &&
          endTime.add(1, 'minute').isAfter(scheduleRange?.end)
        )
      ) {
        const offset = listing.duration / 60 < 30 ? 30 : listing.duration / 60
        setTimeOffsetMinutes((p) => p + offset)
      } else if (
        details.event?.code === 'ArrowLeft' &&
        timeOffsetMinutes > 0 &&
        endTime.add(-1, 'minute').isBefore(scheduleRange?.start)
      ) {
        let offset = scheduleRange?.start.diff(startTime, 'minute') ?? 0
        offset = offset < 30 && offset > 0 ? 30 : offset
        setTimeOffsetMinutes((p) => (p - offset < 0 ? 0 : p - offset))
      }
    },
    [scheduleRange]
  )

  const handleListingHovered = useCallback(
    (_, extraProps) => {
      const { channel, listing } = extraProps
      focusedOnListingsGridRef.current = true
      setFocusedChannel(channel)
      setFocusedListing(listing)
      onListingFocused(listing, true)
    },
    [onListingFocused]
  )

  const handleChannelHovered = useCallback(
    (_, extraProps) => {
      const { channel } = extraProps
      focusedOnListingsGridRef.current = false
      setFocusedChannel(channel)
      onChannelFocused(channel, false)
      onListingFocused(getPlayingListing(channel, baseTime.toDate()), false)
    },
    [baseTime, onChannelFocused, onListingFocused]
  )

  const handleWheel = useCallback(
    throttle((event: React.WheelEvent<HTMLDivElement>) => {
      const deltaY = event.deltaY
      const channelStepSize = CHANNEL_HEIGHT_PX + CHANNEL_ROW_MARGIN_BOTTOM_PX

      if (deltaY > 0) {
        setFirstVisibleChannelIndex((prevIndex) => {
          const targetIndex = Math.min(
            channels.length - 1,
            prevIndex + COMPLETE_CHANNELS_VISIBLE
          )
          if (
            targetIndex > prevIndex &&
            targetIndex >= COMPLETE_CHANNELS_VISIBLE
          ) {
            const offset =
              (targetIndex - COMPLETE_CHANNELS_VISIBLE + 1) * channelStepSize
            setTranslateY(-offset)
            return targetIndex - 1
          }
          return prevIndex
        })
      } else if (deltaY < 0) {
        setFirstVisibleChannelIndex((prevIndex) => {
          const targetIndex = Math.max(0, prevIndex - 1)
          if (targetIndex < prevIndex) {
            const offset =
              (targetIndex - COMPLETE_CHANNELS_VISIBLE + 2) * channelStepSize
            setTranslateY(-offset)
            return targetIndex
          }
          return prevIndex
        })
      }
    }, 600),
    [channels.length]
  )

  function handleHorizontalScrolling(
    direction: 'left' | 'right',
    extraArgs: Record<string, unknown>
  ) {
    const { index } = extraArgs
    const channel = channels[index as number]
    const newScheduleStart = scheduleRange!.start
    let filteredListings

    if (direction === 'right') {
      filteredListings = filterListings(
        channel.listings,
        baseTime,
        newScheduleStart.add(90, 'minute'),
        scheduleRange!.end
      )
      setTimeOffsetMinutes((prev) => prev + 90)
    } else {
      filteredListings = filterListings(
        channel.listings,
        baseTime,
        newScheduleStart.subtract(90, 'minute'),
        scheduleRange!.end
      )
      setTimeOffsetMinutes((prev) => (prev - 90 < 0 ? 0 : prev - 90))
    }
    const listing = filteredListings[0].listing
    setFocusedListing(filteredListings[0].listing)
    onListingFocused(filteredListings[0].listing, true)
    setFocus(`channel-${index}_${listing.show_tms_id}_${listing.start_time}`)
  }

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        className={classNames('epg', { [className]: !!className })}
        ref={ref}
        onWheel={handleWheel}
        style={{
          transition: platformTransitionStyle('transform 400ms ease-in-out'),
        }}
      >
        <TimeLine
          currentTime={baseTime}
          focusedDate={dayjs(focusedListing?.start_time)}
          slotDuration={DEFAULT_SLOT_DURATION_MINUTES}
          onScheduleRangeChange={setScheduleRange}
          hoursToDisplay={DEFAULT_HOURS_TO_DISPLAY}
          timeIndicatorPosition={timeIndicatorPosition}
        />
        <Channels
          channels={channels}
          baseTime={baseTime}
          scheduleRange={scheduleRange}
          timeOffsetMinutes={timeOffsetMinutes}
          translateY={translateY}
          onLoad={() => setIsEPGLoaded(true)}
        >
          {useCallback(
            (channelRow, chIndex) => (
              <ChannelRow
                data={channelRow.channel}
                key={channelRow.channel.feed_cid}
                onFocus={handleChannelFocused}
                onHover={handleChannelHovered}
                onEnterPress={handleChannelEnterPress}
                index={chIndex}
                visibility={
                  firstVisibleChannelIndex - COMPLETE_CHANNELS_VISIBLE + 2 >
                  chIndex
                    ? 'hidden'
                    : 'visible'
                }
                hasHiddenListingAtStart={!!channelRow.hiddenListingAtStart}
                hasFocusedChild={
                  focusedChannel?.feed_cid === channelRow.channel.feed_cid
                }
                handleHorizontalScrolling={handleHorizontalScrolling}
                canScrollLeft={timeOffsetMinutes > 0}
                canScrollRight={scheduleRange?.end.isBefore(
                  dayjs().add(1, 'day').endOf('day')
                )}
              >
                <Schedules
                  timeIndicatorPosition={timeIndicatorPosition}
                  hiddenListingAtStart={!!channelRow.hiddenListingAtStart}
                >
                  {channelRow.filteredListings.map(
                    ({ listing: schedule, position }) => {
                      return (
                        <Schedule
                          key={schedule.show_tms_id + schedule.start_time}
                          focusKey={`channel-${chIndex}_${schedule.show_tms_id}_${schedule.start_time}`}
                          data={schedule}
                          onFocus={handleListingFocused}
                          onHover={handleListingHovered}
                          position={position}
                          scheduleRange={scheduleRange}
                          channel={channelRow.channel}
                          hoursToDisplay={DEFAULT_HOURS_TO_DISPLAY}
                        />
                      )
                    }
                  )}
                </Schedules>
              </ChannelRow>
            ),
            [
              scheduleRange,
              handleChannelFocused,
              handleChannelEnterPress,
              handleListingFocused,
              focusedChannel,
              firstVisibleChannelIndex,
            ]
          )}
        </Channels>
      </div>
    </FocusContext.Provider>
  )
}

export default memo(EPG)
